import { useState, useEffect, useCallback } from "react";
import { faHeartPulse } from "@fortawesome/free-solid-svg-icons";
import type { DevToolsPlugin, PluginStatus } from "../../types";

interface Service {
  name: string;
  running: boolean;
  docker: boolean;
  url: string;
}

interface Repository {
  name: string;
  found: boolean;
  branch?: string;
  commit?: string;
  updated?: string;
  divergence?: string;
}

interface HostEntry {
  hostname: string;
  configured: boolean;
}

interface RaterGem {
  name: string;
  error?: string;
  gems?: Record<string, { version?: string; path?: string }>;
}

interface EnvHealthData {
  services: Service[];
  repositories: Repository[];
  hostnames: HostEntry[];
  rater_gems: RaterGem[];
}

type Status = "ok" | "warn" | "error";

function computeStatus(data: EnvHealthData | null): Status | null {
  if (!data) return null;
  const allServicesOk = (data.services ?? []).every((s) => s.running);
  const hostsOk = (data.hostnames ?? []).every((h) => h.configured);
  const raterGemsOk = (data.rater_gems ?? []).every((s) => !s.error);
  if (allServicesOk && hostsOk && raterGemsOk) return "ok";
  const criticalDown = data.services.filter((s) => !s.running);
  if (criticalDown.length > 0 || !hostsOk) return "error";
  return "warn";
}

const STATUS_LABEL: Record<Status, string> = {
  ok: "All systems OK",
  warn: "Some warnings",
  error: "Issues detected",
};

const INDICATOR_SYMBOL: Record<Status, string> = {
  ok: "\u2713",
  warn: "?",
  error: "\u2715",
};

function Indicator({ status }: { status: Status }) {
  return (
    <span className={`dt-envhealth__indicator dt-envhealth__indicator--${status}`}>
      {INDICATOR_SYMBOL[status]}
    </span>
  );
}

// Module-level cache so data persists across drawer open/close
let cachedData: EnvHealthData | null = null;
let fetchPromise: Promise<void> | null = null;
let listeners: Array<() => void> = [];

function notifyListeners() {
  listeners.forEach((fn) => fn());
}

function fetchHealthData(bustCache = false): Promise<void> {
  if (fetchPromise && !bustCache) return fetchPromise;
  fetchPromise = fetch("/dev_tools/env-health", {
    cache: bustCache ? "no-store" : "default",
  })
    .then((r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    })
    .then((json) => {
      cachedData = json;
      notifyListeners();
    })
    .catch((e) => {
      console.error("Env health fetch error:", e);
    })
    .finally(() => {
      fetchPromise = null;
    });
  return fetchPromise;
}

function EnvHealthPanel() {
  const [, forceUpdate] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const data = cachedData;

  useEffect(() => {
    const listener = () => forceUpdate((n) => n + 1);
    listeners.push(listener);
    return () => {
      listeners = listeners.filter((fn) => fn !== listener);
    };
  }, []);

  const handleRefresh = useCallback(async () => {
    setIsLoading(true);
    await fetchHealthData(true);
    setIsLoading(false);
  }, []);

  const status = computeStatus(data);

  if (!data) {
    return <div className="dt-envhealth__detail">Loading...</div>;
  }

  if (!data || !status) return null;

  return (
    <>
      <div className="dt-envhealth__summary">
        <span className={`dt-envhealth__summary-label dt-envhealth__summary-label--${status}`}>
          {STATUS_LABEL[status]}
        </span>
        <button
          className="dt-btn"
          onClick={handleRefresh}
          disabled={isLoading}
          type="button"
        >
          {isLoading ? "..." : "Refresh"}
        </button>
      </div>

      <ServicesSection services={data.services} title="Local Services" docker={false} />
      <ServicesSection services={data.services} title="Docker Services" docker={true} />
      <RepositoriesSection repositories={data.repositories} />
      <HostsFileSection hostsFile={data.hostnames} />
      <RaterGemsSection raterGems={data.rater_gems} />
    </>
  );
}

function ServicesSection({
  services,
  title,
  docker,
}: {
  services: Service[];
  title: string;
  docker: boolean;
}) {
  const filtered = services.filter((s) => s.docker === docker);
  if (filtered.length === 0) return null;
  return (
    <div className="dt-envhealth__section">
      <div className="dt-envhealth__section-title">{title}</div>
      {filtered.map((s) => (
        <div key={s.name} className="dt-envhealth__row">
          <Indicator status={s.running ? "ok" : "error"} />
          <span className="dt-envhealth__name">{s.name}</span>
          <span className={`dt-envhealth__detail dt-envhealth__detail--${s.running ? "ok" : "error"}`}>
            {s.running ? "running" : "not running"}
          </span>
        </div>
      ))}
    </div>
  );
}

function RepositoriesSection({ repositories }: { repositories: Repository[] }) {
  return (
    <div className="dt-envhealth__section">
      <div className="dt-envhealth__section-title">Repositories</div>
      {repositories.map((r) => {
        if (!r.found) {
          return (
            <div key={r.name} className="dt-envhealth__row">
              <Indicator status="warn" />
              <span className="dt-envhealth__name">{r.name}</span>
              <span className="dt-envhealth__detail">not found</span>
            </div>
          );
        }
        const isAhead = r.divergence && r.divergence !== "(up to date)";
        return (
          <div key={r.name} className="dt-envhealth__row">
            <Indicator status={isAhead ? "warn" : "ok"} />
            <span className="dt-envhealth__name">{r.name}</span>
            <span className="dt-envhealth__detail">
              {r.branch} &middot; {r.commit} &middot; {r.updated} ago &middot; {r.divergence}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function HostsFileSection({ hostsFile }: { hostsFile: HostEntry[] }) {
  return (
    <div className="dt-envhealth__section">
      <div className="dt-envhealth__section-title">/etc/hosts</div>
      {hostsFile.map((h) => (
        <div key={h.hostname} className="dt-envhealth__row">
          <Indicator status={h.configured ? "ok" : "error"} />
          <span className="dt-envhealth__name">{h.hostname}</span>
          <span className={`dt-envhealth__detail dt-envhealth__detail--${h.configured ? "ok" : "error"}`}>
            {h.configured ? "configured" : "missing"}
          </span>
        </div>
      ))}
    </div>
  );
}

function RaterGemsSection({ raterGems }: { raterGems: RaterGem[] }) {
  return (
    <div className="dt-envhealth__section">
      <div className="dt-envhealth__section-title">Rater Gems</div>
      {raterGems.map((service) => {
        if (service.error) {
          return (
            <div key={service.name} className="dt-envhealth__row">
              <Indicator status="error" />
              <span className="dt-envhealth__name">{service.name}</span>
              <span className="dt-envhealth__detail dt-envhealth__detail--error">
                {service.error}
              </span>
            </div>
          );
        }
        return Object.entries(service.gems || {}).map(([gemName, gem]) => (
          <div key={`${service.name}-${gemName}`} className="dt-envhealth__row">
            <Indicator status={gem.version ? "ok" : "warn"} />
            <span className="dt-envhealth__name">{service.name}/{gemName}</span>
            <span className="dt-envhealth__detail">
              {gem.version || "not loaded"}{" "}
              {gem.path && `(${gem.path.split("/").slice(-2).join("/")})`}
            </span>
          </div>
        ));
      })}
    </div>
  );
}

export function envHealthPlugin(): DevToolsPlugin {
  // Fetch eagerly on plugin registration so the icon reflects status immediately
  if (typeof document !== "undefined") fetchHealthData();

  return {
    id: "env-health",
    name: "Env Health",
    icon: faHeartPulse,
    render: () => <EnvHealthPanel />,
    useStatus: (): PluginStatus => {
      // Re-render sidebar when data arrives
      const [, forceUpdate] = useState(0);
      useEffect(() => {
        const listener = () => forceUpdate((n) => n + 1);
        listeners.push(listener);
        return () => {
          listeners = listeners.filter((fn) => fn !== listener);
        };
      }, []);

      const status = computeStatus(cachedData);
      if (!status) return { state: "loading" };
      if (status === "ok") return { state: "success", summary: "All systems OK" };
      if (status === "warn") return { state: "warning", summary: "Some warnings" };
      return { state: "error", badge: "!", summary: "Issues detected" };
    },
  };
}
